import cx from "classnames";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { Container, format, getIsUserSignedIn, getUser, Row } from "@gamedev-web/gamedev-sdk-core";
import { BasicButton, ButtonType, Icon, IconSize } from "@gaming/xds-react-js";
import { ApplicationLaunch, TagPlayStyle } from "@gaming/xds-react-js-icons";

import { FormattedText } from "../../../components/FormattedText";
import { useAuthenticatedNavigation } from "../../../constants";
import { FormDataValidation, validateFormUxOnly } from "../../../helpers/formValidation";
import { getImage, ImageType } from "../../../helpers/imageHelper";
import { useProgramBackgroundColorPicker } from "../../../helpers/useProgramColorPicker";
import { useStrings } from "../../../localization/localizationHelpers";
import { IdXboxApplicationStep } from "../../../models/status";
import { RouteNames } from "../../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../../partnerConfig/routes";
import { OnboardingRootState } from "../../../redux/reducers/rootReducer";
import { getIdXboxApplicationStep, getSubFormData } from "../../../redux/selectors";
import { OnboardingPageAction } from "../../../telemetry/models";
import { logPageAction } from "../../../telemetry/pageAction";
import { IdXboxIntroFormId } from "../../IdXboxIntroPage/IdXboxIntroPageConstants";
import * as styles from "./HeroContent.module.css";

export const HeroContent = () => {
  const [
    landingPageHeroTitle,
    landingPageSignedInHeroTitle,
    landingPageHeroDescription,
    landingPageHeroBecomeAPartnerCTA,
    landingPageHeroReviewApplicationCTA,
    landingPageHeroToolsCallout,
    landingPageHeroToolsCalloutNoBold,
    landingPageHeroViewDocumentationCTA,
    landingPageHeroViewDocumentationAccessibilityCTA,
    landingPageHeroGameCallout,
    landingPageHeroAlreadyAPartnerLink,
    landingPageHeroBecomeAPartnerAccessibilityCTA,
  ] = useStrings(
    "landingPageHeroTitle",
    "landingPageSignedInHeroTitle",
    "landingPageHeroDescription",
    "landingPageHeroBecomeAPartnerCTA",
    "landingPageHeroReviewApplicationCTA",
    "landingPageHeroToolsCallout",
    "landingPageHeroToolsCalloutNoBold",
    "landingPageHeroViewDocumentationCTA",
    "landingPageHeroViewDocumentationAccessibilityCTA",
    "landingPageHeroGameCallout",
    "landingPageHeroAlreadyAPartnerLink",
    "landingPageHeroBecomeAPartnerAccessibilityCTA"
  );

  const signedIn = useSelector(getIsUserSignedIn);
  const userInfo = useSelector(getUser);
  const currentApplicationStep = useSelector(getIdXboxApplicationStep);
  const [callToActionButtonLabel, setCallToActionButtonLabel] = useState(
    landingPageHeroReviewApplicationCTA
  );
  const formId = IdXboxIntroFormId;
  const subFormStudioId = "studio";
  const subFormContactId = "contact";
  const history = useHistory();

  const subFormDataStudio = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, formId, subFormStudioId)
  );
  const subFormDataContact = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, formId, subFormContactId)
  );

  useEffect(() => {
    const checkStatusForm = async () => {
      const formValidationStudio = await validateFormUxOnly(
        subFormDataStudio,
        formId,
        subFormStudioId
      );
      const formValidationContact = await validateFormUxOnly(
        subFormDataContact,
        formId,
        subFormContactId
      );

      if (
        (formValidationStudio === FormDataValidation.NonValidEmpty &&
          formValidationContact === FormDataValidation.NonValidEmpty) ||
        !signedIn
      ) {
        setCallToActionButtonLabel(landingPageHeroBecomeAPartnerCTA);
      }
    };
    if (
      currentApplicationStep === IdXboxApplicationStep.Introduction ||
      currentApplicationStep === IdXboxApplicationStep.Unset
    ) {
      checkStatusForm();
    }
  }, [
    currentApplicationStep,
    formId,
    landingPageHeroBecomeAPartnerCTA,
    signedIn,
    subFormDataContact,
    subFormDataStudio,
  ]);

  const signInUrlWithCallback = useAuthenticatedNavigation(
    OnboardingUrls[RouteNames.ProgramSelection]
  );

  const colorStyle = useProgramBackgroundColorPicker(true);

  const renderProps = {
    title: signedIn
      ? format(landingPageSignedInHeroTitle, userInfo?.firstName || userInfo?.displayName || "")
      : landingPageHeroTitle,
    description: landingPageHeroDescription,
    callToAction: callToActionButtonLabel,
    callToActionUrl: OnboardingUrls.ProgramSelection,
    bottomBarLeftText: landingPageHeroToolsCallout,
    bottomBarLeftTextNoBold: landingPageHeroToolsCalloutNoBold,
    bottomBarRightLinkText: landingPageHeroViewDocumentationCTA,
    bottomBarRightLinkUrl: "https://docs.microsoft.com/gaming/",
    gameCallout: landingPageHeroGameCallout,
    signInText: landingPageHeroAlreadyAPartnerLink,
    signInUrl: signInUrlWithCallback,
  };

  return (
    <section className={styles.heroContainer} role="tabpanel" aria-label={renderProps.title}>
      <picture>
        <div className={styles.heroContainerImage}>
          <img
            src={getImage("onboarding_landing_hero", ImageType.Jpg)}
            alt={landingPageHeroDescription}
          />
          <div className={styles.fadeEffect} />
        </div>
      </picture>
      <div className={styles.heroContent}>
        <Container className={cx("high-contrast", styles.heroInformation)}>
          <Row>
            <h1 className={cx(styles.heroTitle)}>{renderProps.title}</h1>
          </Row>
          <Row>
            <p className={styles.heroDescription}>{renderProps.description}</p>
          </Row>
          <Row className={styles.heroButtonRow}>
            <BasicButton
              tabIndex={0}
              aria-label={landingPageHeroBecomeAPartnerAccessibilityCTA}
              as={"a"}
              buttonType={ButtonType.Brand}
              className={cx(colorStyle, styles.heroButtonBecomeAPartner)}
              onClick={() => {
                logPageAction(OnboardingPageAction.BecomeAPartnerButton);
                history.push(renderProps.callToActionUrl);
              }}
              textDecorationUnderline={true}
            >
              {renderProps.callToAction}
            </BasicButton>
            <a
              href={renderProps.signInUrl}
              className={styles.heroAlreadyAPartnerLink}
              onClick={() => logPageAction(OnboardingPageAction.SignInButton)}
            >
              {renderProps.signInText}
            </a>
            <div className={styles.heroGameInfoSmallDevices}>
              <div className={styles.heroGameInfoTextContainer}>
                <p className={styles.heroGameInfoText}>
                  <FormattedText text={renderProps.gameCallout} />
                </p>
              </div>
            </div>
          </Row>
        </Container>
      </div>
      <div className={styles.heroBottomBar}>
        <Container className={styles.heroBottomBarContainer}>
          <div className={styles.heroBottomBarLeftContent}>
            <p className={styles.heroBottomBarLeftContentNonBold}>
              <FormattedText text={renderProps.bottomBarLeftText} />
            </p>
            <span className={styles.heroBottomBarLeftContentNonBoldSmallDevice}>
              {renderProps.bottomBarLeftTextNoBold}
            </span>
            <span className={styles.bottomBarRightLinkTextSmallDevices}>
              <a
                className={styles.linkColor}
                href={renderProps.bottomBarRightLinkUrl}
                target="_new"
              >
                {renderProps.bottomBarRightLinkText}
              </a>
            </span>
          </div>
          <div className={styles.heroBottomBarRightContent}>
            <a
              href={renderProps.bottomBarRightLinkUrl}
              target="_new"
              aria-label={landingPageHeroViewDocumentationAccessibilityCTA}
            >
              {renderProps.bottomBarRightLinkText}{" "}
              <Icon
                aria-hidden={true}
                className={styles.heroBottomBarRightContentIcon}
                icon={ApplicationLaunch}
                iconSize={IconSize.Custom}
              />
            </a>
          </div>
        </Container>
      </div>
      <div className={styles.heroGameInfo}>
        <Icon aria-hidden="true" icon={TagPlayStyle} iconSize={IconSize.Custom} />
        <div className={styles.heroGameInfoTextContainer}>
          <p className={styles.heroGameInfoText}>
            <FormattedText text={renderProps.gameCallout} />
          </p>
        </div>
      </div>
    </section>
  );
};
